import { Building2, CalendarDays, Edit2, Plus, Search, Settings, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  deleteSchedulerAlertConfig,
  fetchSchedulerAlertConfigFormOptions,
  fetchSchedulerAlertConfigList,
  fetchSchedulerContentReviewById,
  saveSchedulerAlertConfig,
  type SchedulerAlertConfigFormOptions,
  type SchedulerAlertConfigItem,
  type SchedulerAlertConfigListPayload,
  type SchedulerAlertConfigSavePayload,
  type SchedulerContentReviewItem,
} from "../../api/frameworksApi";
import {
  SCHEDULER_MODULE_NAME,
  defaultCriteriaDraft,
  formatDateTime,
  formatEnumLabel,
} from "../../stores/schedulersStore";
import { validateRequired } from "../../utils/validators";
import { Breadcrumb } from "../components/Breadcrumb";
import { DataTable, type Column } from "../components/DataTable";
import { RowsPerCardSelect } from "../components/EntityCardGrid";
import { PageShell } from "../components/layout/PageShell";
import {
  FormField,
  FormInput,
  FormSelect,
  Modal,
  ModalFooterButtons,
} from "../components/Modal";
import { showToast } from "../components/ui/custom-toast";
import { Tip } from "../components/ui/tooltip";

type CriteriaDraft = ReturnType<typeof defaultCriteriaDraft>;

/** UI keys sortable in the table, mapped to the API's `sortByColumn` values. */
const SORT_COLUMN_MAP: Record<string, string> = {
  organization: "orgName",
  documentCategory: "documentCategory",
  documentType: "documentType",
  poam: "leadAmount",
  updatedAt: "updatedAt",
};

/** Draft seeded from the form-options API's first category/lead-unit, so the
 *  dropdowns never default to a value the server doesn't actually accept.
 *  Organization is always the scheduler's own org - never user-editable. */
function firstDraftFromOptions(
  formOptions: SchedulerAlertConfigFormOptions | null,
  schedule: SchedulerContentReviewItem | null,
): CriteriaDraft {
  const base = defaultCriteriaDraft();
  const category = formOptions?.categories[0];
  const leadUnit = formOptions?.leadUnits[0];
  return {
    ...base,
    organization: schedule?.orgId ?? base.organization,
    documentCategory: category?.value ?? base.documentCategory,
    documentType: category?.documentTypes[0]?.value ?? "",
    poamLeadValue: "1",
    poamLeadUnit: leadUnit?.value ?? base.poamLeadUnit,
  };
}

export function SchedulerConfiguration() {
  const { id } = useParams<{ id: string }>();

  const [schedule, setSchedule] = useState<SchedulerContentReviewItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [rows, setRows] = useState<SchedulerAlertConfigItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTarget, setEditTarget] = useState<SchedulerAlertConfigItem | null>(null);
  const [draft, setDraft] = useState<CriteriaDraft>(defaultCriteriaDraft());
  const [formErrors, setFormErrors] = useState<{
    documentCategory?: string;
    documentType?: string;
    poamLeadValue?: string;
  }>({});

  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SchedulerAlertConfigItem | null>(null);

  const [formOptions, setFormOptions] = useState<SchedulerAlertConfigFormOptions | null>(null);
  const [formOptionsLoading, setFormOptionsLoading] = useState(false);

  // Category/type/lead-unit dropdown options - reads no tenant data, so the
  // API response is cached for the session; safe to fetch once here.
  useEffect(() => {
    let cancelled = false;
    setFormOptionsLoading(true);
    fetchSchedulerAlertConfigFormOptions()
      .then((res) => {
        if (!cancelled) setFormOptions(res.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setFormOptions(null);
      })
      .finally(() => {
        if (!cancelled) setFormOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCategoryTypes =
    formOptions?.categories.find((c) => c.value === draft.documentCategory)?.documentTypes ?? [];
  const selectedLeadUnit = formOptions?.leadUnits.find((u) => u.value === draft.poamLeadUnit);

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // Reset all local UI state whenever we land on a different scheduler, then
  // fetch it by id (GET /scheduler/content-review/{id}).
  useEffect(() => {
    setPage(0);
    setPageSize(10);
    setSearch("");
    setDebouncedSearch("");
    setSortKey(null);
    setSortDir("asc");
    setShowForm(false);
    setEditTarget(null);
    setDraft(defaultCriteriaDraft());
    setFormErrors({});
    setShowDelete(false);
    setDeleteTarget(null);

    setSchedule(null);
    setLoadError(null);
    setRows([]);
    setTotalItems(0);
    if (!id) return;

    let cancelled = false;
    setLoading(true);
    fetchSchedulerContentReviewById(id, SCHEDULER_MODULE_NAME)
      .then((res) => {
        if (cancelled) return;
        if (res.status !== "success" || !res.data) {
          throw new Error("Scheduler configuration not found.");
        }
        setSchedule(res.data);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err?.message || "Scheduler configuration not found.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  // Debounce free-text search before it goes to the server.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchCriteria = useCallback(async () => {
    if (!schedule) return;
    try {
      setTableLoading(true);
      setTableError(null);

      const payload: SchedulerAlertConfigListPayload = { orgId: schedule.orgId };
      if (debouncedSearch) payload.keyword = debouncedSearch;
      if (sortKey) {
        payload.sortByColumn = SORT_COLUMN_MAP[sortKey] ?? sortKey;
        payload.sortingOrder = sortDir;
      }

      const res = await fetchSchedulerAlertConfigList(page, pageSize, payload);
      if (res.status !== "success") {
        throw new Error("Failed to load configuration.");
      }
      setRows(res.data ?? []);
      setTotalItems(res.pagination?.totalCount ?? (res.data ?? []).length);
    } catch (err: any) {
      setTableError(err?.message || "Failed to load configuration.");
      setRows([]);
      setTotalItems(0);
    } finally {
      setTableLoading(false);
    }
  }, [schedule, page, pageSize, debouncedSearch, sortKey, sortDir]);

  useEffect(() => {
    fetchCriteria();
  }, [fetchCriteria]);

  const openCreate = () => {
    setEditTarget(null);
    setDraft(firstDraftFromOptions(formOptions, schedule));
    setFormErrors({});
    setShowForm(true);
  };

  const openEdit = (row: SchedulerAlertConfigItem) => {
    setEditTarget(row);
    setDraft({
      organization: row.orgId,
      documentCategory: row.documentCategory,
      documentType: row.documentType ?? "",
      poamLeadValue: String(row.leadAmount),
      poamLeadUnit: row.leadUnit,
    });
    setFormErrors({});
    setShowForm(true);
  };

  const validateForm = (): boolean => {
    const documentCategoryCheck = validateRequired(draft.documentCategory, "Document category");
    // Whether this category takes a document type is server-declared (an
    // empty `documentTypes` array), never hard-coded to a category name.
    const documentTypeCheck =
      selectedCategoryTypes.length === 0
        ? { valid: true }
        : validateRequired(draft.documentType, "Document type");
    const poamLeadValueCheck =
      !draft.poamLeadValue || !draft.poamLeadValue.trim()
        ? { valid: false, error: "POA&M lead value is required" }
        : Number(draft.poamLeadValue) < 1
          ? { valid: false, error: "POA&M lead value must be at least 1" }
          : selectedLeadUnit && Number(draft.poamLeadValue) > selectedLeadUnit.maxAmount
            ? { valid: false, error: `Must be at most ${selectedLeadUnit.maxAmount}` }
            : { valid: true };

    const nextErrors: typeof formErrors = {};
    if (!documentCategoryCheck.valid) nextErrors.documentCategory = documentCategoryCheck.error;
    if (!documentTypeCheck.valid) nextErrors.documentType = documentTypeCheck.error;
    if (!poamLeadValueCheck.valid) nextErrors.poamLeadValue = poamLeadValueCheck.error;

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!schedule) return;
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload: SchedulerAlertConfigSavePayload = {
        id: editTarget ? editTarget.id : null,
        orgId: schedule.orgId,
        documentCategory: draft.documentCategory,
        ...(selectedCategoryTypes.length === 0 ? {} : { documentType: draft.documentType }),
        leadAmount: Number(draft.poamLeadValue) || 0,
        leadUnit: draft.poamLeadUnit,
        enabled: editTarget ? editTarget.enabled : true,
      };
      const res = await saveSchedulerAlertConfig(payload);
      if (res.status !== "success" || !res.data) {
        throw new Error("Failed to save configuration.");
      }

      if (editTarget) {
        setRows((prev) => prev.map((r) => (r.id === res.data.id ? res.data : r)));
        showToast("success", "Configuration updated.");
      } else {
        setRows((prev) => [res.data, ...prev]);
        setTotalItems((t) => t + 1);
        showToast("success", "Configuration created.");
      }
      setShowForm(false);
    } catch (err: any) {
      showToast("error", err?.message || "Failed to save configuration.");
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (row: SchedulerAlertConfigItem) => {
    setDeleteTarget(row);
    setShowDelete(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteSchedulerAlertConfig(deleteTarget.id);
      if (res.status !== "success") {
        throw new Error("Failed to delete configuration.");
      }
      setRows((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setTotalItems((t) => Math.max(0, t - 1));
      showToast("success", "Configuration deleted.");
      setShowDelete(false);
    } catch (err: any) {
      showToast("error", err?.message || "Failed to delete configuration.");
    } finally {
      setDeleting(false);
    }
  };

  // Row's own label fields cover normal display; these only backfill locally
  // fabricated rows or types outside the currently-selected category.
  const documentTypeLabel = (value: string): string => {
    for (const category of formOptions?.categories ?? []) {
      const match = category.documentTypes.find((t) => t.value === value);
      if (match) return match.label;
    }
    return formatEnumLabel(value);
  };
  const leadUnitLabel = (value: string): string =>
    formOptions?.leadUnits.find((u) => u.value === value)?.label ?? formatEnumLabel(value);

  const columns: Column<SchedulerAlertConfigItem>[] = [
    {
      key: "sno",
      header: "#",
      align: "center",
      render: (_r, idx) => (
        <span className="text-xs font-medium text-[#94a3b8] dark:text-white/40">
          {page * pageSize + idx + 1}
        </span>
      ),
    },
    {
      key: "organization",
      header: "Organization",
      align: "left",
      sortable: true,
      render: (r) => (
        <div className="flex items-center gap-1.5 min-w-0">
          <Building2 className="size-3.5 flex-shrink-0 text-[#64748b] dark:text-white/40" />
          <span className="text-[12.5px] font-medium text-[#0f172a] dark:text-white/80 truncate">
            {r.orgName}
          </span>
        </div>
      ),
    },
    {
      key: "documentCategory",
      header: "Category",
      align: "center",
      sortable: true,
      render: (r) => (
        <span className="text-[12.5px] font-medium text-[#0f172a] dark:text-white/80">
          {r.documentCategoryLabel || formatEnumLabel(r.documentCategory)}
        </span>
      ),
    },
    {
      key: "documentType",
      header: "Document Type",
      align: "center",
      sortable: true,
      render: (r) =>
        !r.documentType ? (
          <span className="text-[12.5px] text-[#94a3b8] dark:text-white/40">-</span>
        ) : (
          <span className="text-[12.5px] font-medium text-[#0f172a] dark:text-white/80">
            {documentTypeLabel(r.documentType)}
          </span>
        ),
    },
    {
      key: "poam",
      header: "Alert Before",
      align: "center",
      sortable: true,
      render: (r) => (
        <span className="text-[12.5px] font-medium text-[#0f172a] dark:text-white/80">
          {r.leadAmount} {r.leadUnitLabel || leadUnitLabel(r.leadUnit)}
        </span>
      ),
    },
    {
      key: "updatedAt",
      header: "Last Updated",
      align: "center",
      sortable: true,
      render: (r) => (
        <span className="inline-flex items-center gap-1 text-[12.5px] font-medium text-[#0f172a] dark:text-white/80">
          <CalendarDays className="size-3.5 text-[#94a3b8] dark:text-white/40" />
          {formatDateTime(r.updatedAt)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "center",
      render: (r) => (
        <div className="flex items-center justify-center gap-1">
          <Tip label="Edit configuration" side="bottom">
            <button
              onClick={() => openEdit(r)}
              className="p-1.5 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 rounded transition-colors"
            >
              <Edit2 className="size-4 d hover:text-indigo-600" />
            </button>
          </Tip>
          <Tip label="Delete configuration" side="bottom">
            <button
              onClick={() => openDelete(r)}
              className="p-1.5 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
            >
              <Trash2 className="size-4 d hover:text-red-600" />
            </button>
          </Tip>
        </div>
      ),
    },
  ];

  return (
    <PageShell className="p-6 space-y-6">
      <Breadcrumb
        parent="Schedulers"
        parentTo="/administration/schedulers"
        current={
          schedule
            ? `${formatEnumLabel(schedule.moduleName).replace(/\s+Review$/i, "")} Configuration`
            : "Configuration"
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#A78BFA]/10 flex items-center justify-center">
            <Settings className="w-5 h-5 text-[#A78BFA]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Review Alert Configuration</h1>
          </div>
        </div>
        {schedule && (
          <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
            <button
              type="button"
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="size-4" />
              New Configuration
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center gap-2 py-16">
          <div className="size-10 rounded-full bg-[#f1f5f9] dark:bg-white/[0.05] flex items-center justify-center">
            <Settings className="size-4 text-[#64748b] dark:text-white/50 animate-pulse" />
          </div>
          <p className="text-[13px] font-medium text-[#475569] dark:text-white/50">
            Loading scheduler...
          </p>
        </div>
      )}

      {!loading && schedule && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[200px] flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d" />
              <input
                type="text"
                placeholder="Search configuration"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm"
              />
            </div>

            <div className="ml-auto">
              <RowsPerCardSelect
                value={pageSize}
                onChange={(size) => {
                  setPageSize(size);
                  setPage(0);
                }}
                label="Rows per page"
              />
            </div>
          </div>

          <DataTable
            columns={columns}
            data={rows}
            loading={tableLoading}
            error={tableError}
            onRetry={fetchCriteria}
            rowKey={(r) => r.id}
            rowClassName={() => "hover:bg-indigo-50/50 dark:hover:bg-white/[0.05]"}
            emptyText={search.trim() ? "No configuration entries match your search." : "No configuration entries yet."}
            emptyIcon={search.trim() ? (
              <Search className="size-4 text-[#64748b] dark:text-white/40" />
            ) : (
              <Settings className="size-4 text-[#64748b] dark:text-white/40" />
            )}
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setPage}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
          />
        </div>
      )}

      {!loading && !schedule && (
        <div className="flex flex-col items-center justify-center gap-2 py-16">
          <div className="size-10 rounded-full bg-[#f1f5f9] dark:bg-white/[0.05] flex items-center justify-center">
            <Settings className="size-4 text-[#64748b] dark:text-white/50" />
          </div>
          <p className="text-[13px] font-medium text-[#475569] dark:text-white/50">
            {loadError || "This scheduler could not be found."}
          </p>
        </div>
      )}

      {/* Add / Edit modal — same fields as the previous Configure popup */}
      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editTarget ? "Edit Configuration" : "Add Configuration"}
        footer={
          <ModalFooterButtons
            onCancel={() => setShowForm(false)}
            onSubmit={handleSave}
            submitLabel={saving ? "Saving..." : "Save"}
            submitDisabled={saving || formOptionsLoading}
          />
        }
      >
        <div className="space-y-3.5">
          <FormField label="Organization">
            <div className="px-3 py-2 rounded-lg bg-muted border border-border text-sm text-muted-foreground">
              {schedule?.orgName ?? "-"}
            </div>
          </FormField>

          <div className="grid grid-cols-2 gap-3.5">
            <FormField label="Document category" required error={formErrors.documentCategory}>
              <FormSelect
                value={draft.documentCategory}
                onChange={(v) => {
                  const nextTypes =
                    formOptions?.categories.find((c) => c.value === v)?.documentTypes ?? [];
                  setDraft((d) => ({
                    ...d,
                    documentCategory: v,
                    // Server-declared: an empty documentTypes list for the
                    // newly selected category means it takes no type at all.
                    documentType: nextTypes[0]?.value ?? "",
                  }));
                  setFormErrors((e) => ({ ...e, documentCategory: undefined, documentType: undefined }));
                }}
                options={(formOptions?.categories ?? []).map((c) => ({ value: c.value, label: c.label }))}
                placeholder={formOptionsLoading ? "Loading..." : undefined}
              />
            </FormField>

            {selectedCategoryTypes.length === 0 ? (
              <FormField label="Document type">
                <div className="px-3 py-2 rounded-lg bg-muted border border-border text-sm text-muted-foreground">
                  Not applicable
                </div>
              </FormField>
            ) : (
              <FormField label="Document type" required error={formErrors.documentType}>
                <FormSelect
                  value={draft.documentType}
                  onChange={(v) => {
                    setDraft((d) => ({ ...d, documentType: v }));
                    setFormErrors((e) => ({ ...e, documentType: undefined }));
                  }}
                  options={selectedCategoryTypes.map((t) => ({ value: t.value, label: t.label }))}
                />
              </FormField>
            )}
          </div>

          <FormField
            label="Alert Before"
            required
            error={formErrors.poamLeadValue}
            hint={
              formErrors.poamLeadValue
                ? undefined
                : selectedLeadUnit
                  ? `Counted back from the document's review date. Max ${selectedLeadUnit.maxAmount}.`
                  : "Counted back from the document's review date."
            }
          >
            <div className="grid grid-cols-[7rem_1fr] gap-3">
              <FormInput
                type="number"
                min={1}
                max={selectedLeadUnit?.maxAmount}
                value={draft.poamLeadValue}
                onChange={(v) => {
                  const capped =
                    v === ""
                      ? ""
                      : String(
                        Math.max(
                          1,
                          selectedLeadUnit
                            ? Math.min(Number(v), selectedLeadUnit.maxAmount)
                            : Number(v),
                        ),
                      );
                  setDraft((d) => ({ ...d, poamLeadValue: capped }));
                  setFormErrors((e) => ({ ...e, poamLeadValue: undefined }));
                }}
              />
              <FormSelect
                value={draft.poamLeadUnit}
                onChange={(v) => {
                  const unit = formOptions?.leadUnits.find((u) => u.value === v);
                  setDraft((d) => ({
                    ...d,
                    poamLeadUnit: v,
                    poamLeadValue:
                      unit && Number(d.poamLeadValue) > unit.maxAmount
                        ? String(unit.maxAmount)
                        : d.poamLeadValue,
                  }));
                }}
                options={(formOptions?.leadUnits ?? []).map((u) => ({ value: u.value, label: u.label }))}
                placeholder={formOptionsLoading ? "Loading..." : undefined}
              />
            </div>
          </FormField>
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        open={showDelete}
        onClose={() => setShowDelete(false)}
        title="Delete configuration?"
        footer={
          <div className="flex w-full items-center justify-end gap-3">
            <button
              onClick={() => setShowDelete(false)}
              disabled={deleting}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors shadow-md shadow-red-500/20 disabled:opacity-40"
            >
              {deleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        }
      >
        <p className="text-sm text-[#475569] dark:text-white/60">
          This configuration entry will be removed from the scheduler. This cannot be undone.
        </p>
      </Modal>
    </PageShell>
  );
}
